/**
 * A tela 5 do protótipo: confirmar que o ambiente do cliente está liberado.
 *
 * **Por que este ato tem peso diferente de todos os outros do console.** Ele é um dos dois marcos
 * do relógio contratual: a data só nasce depois do aceite completo do kickoff. O
 * backend já trata isso como ato próprio — rota própria, permissão própria (`ambiente:liberar`,
 * que a matriz da spec §4 reserva à infraestrutura e ao admin interno), evento próprio na esteira
 * — e uma tela que o oferecesse como mais um botão apagaria essa diferença justamente onde ela é
 * lida por uma pessoa.
 *
 * **Confirmação em dois passos, e não um `confirm()`.** É o mesmo desenho do descarte do cofre, e
 * pela mesma razão: o diálogo do navegador não diz o que está prestes a acontecer com as palavras
 * deste portal. O segundo passo é a diferença entre um clique errado e uma decisão — e aqui o
 * clique errado é visível para o cliente no mesmo instante.
 *
 * **A tela não prevê a data de entrega.** `entrega_prevista` é coluna GERADA no banco
 * (`GREATEST(contrato_assinado_em, ambiente_liberado_em, kickoff_aceito_em) + prazo_dias`), e a regra do portal é
 * que nenhuma tela transforme estimativa em promessa: `textoDoPrazo` é a única função autorizada
 * a falar de prazo, e ela não inventa data. A confirmação explica que ainda falta o aceite.
 *
 * **Não existe desfazer.** Nenhuma rota do portal apaga `ambiente_liberado_em`; o serviço recusa
 * uma segunda liberação com `409`. O texto diz isso em vez de deixar quem clicou descobrir depois.
 *
 * **As respostas da infraestrutura moram no mesmo cartão do botão** (spec de 28/09, 6.3). Antes
 * elas só existiam na revisão do kickoff, que a infraestrutura — quem libera — nem abre: decidia
 * sem a informação na frente. Aqui elas aparecem em leitura, com a credencial dita como "guardada
 * no cofre" e um caminho para o cofre, nunca o valor. Se o cliente ainda não enviou o questionário,
 * o cartão diz isso ao lado do botão; o botão continua como era — o aviso informa, não trava.
 */

import { useState } from "react";
import { Link } from "react-router";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import {
  hojeEmBrasilia,
  mensagemDoConsole,
  useLiberarAmbiente,
  useQuestionarioDeInfraestrutura,
  type QuestionarioDeInfraestrutura,
} from "../../nucleo/console";
import { caminhoDoCofre, formatarInstante } from "../../nucleo/cofre";
import { formatarData, type PrazoDaEsteira } from "../../nucleo/implantacoes";

export interface PropsDaLiberacao {
  implantacaoId: string;
  prazo: PrazoDaEsteira;
  /** Nome do cliente — para a confirmação identificar a implantação. */
  cliente: string;
  /** Se a sessão tem `ambiente:liberar`. Falso troca o botão pela explicação de quem libera. */
  podeLiberar: boolean;
  /** Se a sessão tem `infra:ver`. Falso troca as respostas da infraestrutura por um aviso. */
  podeVerInfra: boolean;
  /** No cabeçalho da implantação (SUS-15400): uma linha, com as respostas recolhidas. */
  compacta?: boolean;
}

const MAXIMO_DA_OBSERVACAO = 500;

export function LiberacaoDoAmbiente({
  implantacaoId,
  prazo,
  cliente,
  podeLiberar,
  podeVerInfra,
  compacta = false,
}: PropsDaLiberacao) {
  const [confirmando, setConfirmando] = useState(false);
  const liberadoJa = prazo.ambiente_liberado_em !== null;
  /* Depois da liberação a decisão já foi tomada, e as respostas continuam na revisão: a consulta
     só existe enquanto há decisão a tomar. */
  const infra = useQuestionarioDeInfraestrutura(implantacaoId, podeVerInfra && !liberadoJa);
  const [liberadoEm, setLiberadoEm] = useState(hojeEmBrasilia);
  const [observacao, setObservacao] = useState("");
  const liberacao = useLiberarAmbiente(implantacaoId);

  const liberado = prazo.ambiente_liberado_em !== null;
  const quando = formatarData(prazo.ambiente_liberado_em);
  const entrega = formatarData(prazo.entrega_prevista);

  if (liberado) {
    return (
      <Cartao
        titulo="Ambiente liberado"
        data-testid="liberacao-do-ambiente"
        data-liberado="sim"
        acessorio={
          prazo.relogio_iniciado ? (
            <Chip estado="ok">relógio correndo</Chip>
          ) : (
            <Chip estado="aguardando">aguardando geração da tarefa de kickoff</Chip>
          )
        }
      >
        <p className="cartao__texto">
          O ambiente foi liberado em <b className="numero-tabular">{quando}</b>. Os {prazo.prazo_dias}
          {" "}dias do contrato só começam após o aceite completo do kickoff.{" "}
          {entrega ? (
            <>
              A entrega prevista é <b className="numero-tabular">{entrega}</b>
              {prazo.dias_restantes !== null ? (
                <>
                  {" "}
                  —{" "}
                  {prazo.dias_restantes >= 0
                    ? `faltam ${prazo.dias_restantes} dias`
                    : `${Math.abs(prazo.dias_restantes)} dias em atraso`}
                </>
              ) : null}
              .
            </>
          ) : (
            <>Até a geração da tarefa, a entrega prevista permanece &ldquo;a definir&rdquo;.</>
          )}
        </p>
        <p className="nota-rodape mt-2">
          A liberação não se desfaz nem se repete. Se a data está errada, peça a correção à
          equipe responsável pelo portal — ela fica registrada.
        </p>
      </Cartao>
    );
  }

  /* No cabeçalho, a decisão cabe numa linha e as respostas ficam a um clique: abertas, elas
     empurravam a esteira e os cards para fora da tela (critério de 1366 × 768 da SUS-15400). O
     aviso de questionário não enviado continua à vista, porque é ele que pede cuidado antes de
     liberar. Confirmar abre o cartão inteiro de sempre. */
  if (compacta && (!podeLiberar || !confirmando)) {
    const erroCompacto = liberacao.isError ? mensagemDoConsole(liberacao.error) : null;
    return (
      <section
        className="liberacao-compacta"
        aria-labelledby="titulo-da-liberacao-compacta"
        data-testid="liberacao-do-ambiente"
        data-liberado="nao"
      >
        <div className="liberacao-compacta__linha">
          <h3 className="liberacao-compacta__titulo" id="titulo-da-liberacao-compacta">
            Liberação do ambiente
          </h3>
          <Chip estado="aguardando">o relógio não começou</Chip>
          {/* A regra do prazo já está no resumo ao lado; aqui fica só o que é desta decisão. */}
          {podeLiberar ? null : (
            <p className="liberacao-compacta__texto" data-testid="sem-permissao-de-liberar">
              <b>Quem confirma a liberação é a infraestrutura.</b> Até lá, o cliente lê
              &ldquo;a definir&rdquo; na entrega prevista.
            </p>
          )}
          {podeLiberar ? (
            <Botao
              tamanho="pequeno"
              data-testid="pedir-liberacao"
              onClick={() => {
                liberacao.reset();
                setLiberadoEm(hojeEmBrasilia());
                setConfirmando(true);
              }}
            >
              Liberar o ambiente
            </Botao>
          ) : null}
        </div>
        <AvisoDeInfraNaoEnviada consulta={infra} podeVer={podeVerInfra} />
        {erroCompacto ? (
          <Aviso tom="erro" className="mt-3" data-testid="erro-da-liberacao">
            {erroCompacto}
          </Aviso>
        ) : null}
        <details className="liberacao-compacta__respostas">
          <summary data-testid="ver-respostas-da-infra">
            O que o cliente respondeu sobre a infraestrutura
          </summary>
          <RespostasDaInfraestrutura
            implantacaoId={implantacaoId}
            podeVer={podeVerInfra}
            consulta={infra}
          />
        </details>
      </section>
    );
  }

  if (!podeLiberar) {
    return (
      <Cartao
        titulo="Liberação do ambiente"
        data-testid="liberacao-do-ambiente"
        data-liberado="nao"
        acessorio={<Chip estado="aguardando">o relógio não começou</Chip>}
      >
        <Aviso tom="marca" data-testid="sem-permissao-de-liberar">
          <b>Quem confirma a liberação é a infraestrutura.</b> Só ela e a administração do console
          liberam o ambiente. O prazo contratual depende também da geração da tarefa de kickoff.
        </Aviso>
        <p className="cartao__texto mt-3">
          Enquanto isso não acontecer, o cliente lê <b>&ldquo;a definir&rdquo;</b> no lugar da
          entrega prevista — que é a informação certa, e não uma estimativa.
        </p>
        <RespostasDaInfraestrutura
          implantacaoId={implantacaoId}
          podeVer={podeVerInfra}
          consulta={infra}
        />
      </Cartao>
    );
  }

  const erro = liberacao.isError ? mensagemDoConsole(liberacao.error) : null;

  if (!confirmando) {
    return (
      <Cartao
        titulo="Liberação do ambiente"
        data-testid="liberacao-do-ambiente"
        data-liberado="nao"
        acessorio={<Chip estado="aguardando">o relógio não começou</Chip>}
      >
        <p className="cartao__texto">
          Os {prazo.prazo_dias} dias do contrato começam depois da liberação do ambiente
          <b> e do aceite completo do kickoff</b>. Até os dois marcos, o cliente vê
          &ldquo;a definir&rdquo; no lugar da entrega prevista.
        </p>
        <AvisoDeInfraNaoEnviada consulta={infra} podeVer={podeVerInfra} />
        {erro ? (
          <Aviso tom="erro" className="mt-3" data-testid="erro-da-liberacao">
            {erro}
          </Aviso>
        ) : null}
        <div className="acoes-do-console mt-3">
          <Botao
            data-testid="pedir-liberacao"
            onClick={() => {
              liberacao.reset();
              setLiberadoEm(hojeEmBrasilia());
              setConfirmando(true);
            }}
          >
            Liberar o ambiente
          </Botao>
        </div>
        <RespostasDaInfraestrutura
          implantacaoId={implantacaoId}
          podeVer={podeVerInfra}
          consulta={infra}
        />
      </Cartao>
    );
  }

  return (
    <Cartao
      titulo="Confirmar a liberação do ambiente"
      className="cartao--grave"
      data-testid="liberacao-do-ambiente"
      data-liberado="nao"
      acessorio={<Chip estado="atencao">confirmação</Chip>}
    >
      <div data-testid="confirmacao-da-liberacao">
        <p className="cartao__texto" role="status">
          <b>Confirmar que o ambiente de {cliente} está liberado?</b> Este é o primeiro marco do
          prazo contratual. Os {prazo.prazo_dias} dias só começam depois da geração da tarefa de
          kickoff; até lá a <b>entrega prevista</b> continua &ldquo;a definir&rdquo;. A liberação
          entra na linha do tempo com o seu nome.
        </p>
        <AvisoDeInfraNaoEnviada consulta={infra} podeVer={podeVerInfra} />
        <p className="nota-rodape mb-3">
          <b>Não existe desfazer.</b> A liberação não pode ser apagada nem repetida. Confirme com o
          ambiente de fato entregue — não com ele &ldquo;quase pronto&rdquo;.
        </p>

        <div className="grade-da-liberacao">
          <Campo
            rotulo="Data da liberação"
            type="date"
            value={liberadoEm}
            max={hojeEmBrasilia()}
            onChange={(evento) => setLiberadoEm(evento.target.value)}
            ajuda="A data em que o ambiente ficou disponível. Não pode estar no futuro — o registro é de um ambiente entregue, não de uma promessa."
            data-testid="data-da-liberacao"
          />
          <CampoLongo
            rotulo="Observação (opcional)"
            rows={3}
            maxLength={MAXIMO_DA_OBSERVACAO}
            value={observacao}
            onChange={(evento) => setObservacao(evento.target.value)}
            ajuda="Vai para a linha do tempo, junto do registro. Ex.: o que foi entregue, com quem foi validado."
            data-testid="observacao-da-liberacao"
          />
        </div>

        {erro ? (
          <Aviso tom="erro" className="mt-3" data-testid="erro-da-liberacao">
            {erro}
          </Aviso>
        ) : null}

        <div className="acoes-do-console mt-3">
          <Botao
            data-testid="confirmar-liberacao"
            disabled={liberacao.isPending || liberadoEm.length === 0}
            onClick={() =>
              liberacao.mutate(
                {
                  liberado_em: liberadoEm || null,
                  observacao: observacao.trim() || null,
                },
                { onSuccess: () => setConfirmando(false) },
              )
            }
          >
            {liberacao.isPending ? "Liberando…" : "Confirmar liberação"}
          </Botao>
          <Botao
            variante="fantasma"
            data-testid="cancelar-liberacao"
            disabled={liberacao.isPending}
            onClick={() => {
              liberacao.reset();
              setConfirmando(false);
            }}
          >
            Cancelar
          </Botao>
        </div>
      </div>
      <RespostasDaInfraestrutura
        implantacaoId={implantacaoId}
        podeVer={podeVerInfra}
        consulta={infra}
      />
    </Cartao>
  );
}

type ConsultaDaInfra = ReturnType<typeof useQuestionarioDeInfraestrutura>;

/** Enviado = o cliente fechou o questionário. Qualquer outro estado é "ainda não enviou". */
function infraEnviada(dados: QuestionarioDeInfraestrutura): boolean {
  return dados.disponivel && dados.estado === "enviada";
}

/**
 * O aviso ao lado do botão quando o cliente ainda não enviou o questionário de infraestrutura.
 *
 * Informa e não trava: liberar sem o questionário pode ser legítimo (ambiente na nuvem da
 * GEOvendas, dados combinados em reunião), e o botão continua o mesmo de antes.
 */
function AvisoDeInfraNaoEnviada({
  consulta,
  podeVer,
}: {
  consulta: ConsultaDaInfra;
  podeVer: boolean;
}) {
  if (!podeVer || !consulta.data || infraEnviada(consulta.data)) return null;
  return (
    <Aviso tom="atencao" className="mt-3 mb-3" data-testid="infra-nao-enviada">
      <b>O cliente ainda não enviou o questionário de infraestrutura.</b> Confira as respostas
      abaixo antes de liberar — elas ainda podem mudar.
    </Aviso>
  );
}

/**
 * As respostas do questionário de infraestrutura, em leitura, dentro do cartão da liberação.
 *
 * O texto vem pronto do servidor, pela mesma tradução do resumo do Kick Off. A credencial aparece
 * como "Guardada no cofre" com o caminho para o cofre — revelar tem tela própria, com motivo e
 * registro, e não acontece no meio de uma decisão de liberação.
 */
function RespostasDaInfraestrutura({
  implantacaoId,
  podeVer,
  consulta,
}: {
  implantacaoId: string;
  podeVer: boolean;
  consulta: ConsultaDaInfra;
}) {
  if (!podeVer) {
    return (
      <Aviso tom="marca" className="mt-4" data-testid="sem-acesso-as-respostas-da-infra">
        <b>Seu acesso não inclui as respostas de infraestrutura do cliente.</b> Quem cuida da
        infraestrutura as vê aqui, ao lado da liberação.
      </Aviso>
    );
  }

  return (
    <section
      className="respostas-da-infra mt-4"
      aria-labelledby="titulo-das-respostas-da-infra"
      data-testid="respostas-da-infra"
    >
      <h4 className="campo__rotulo" id="titulo-das-respostas-da-infra">
        O que o cliente respondeu sobre a infraestrutura
      </h4>
      <CorpoDasRespostas implantacaoId={implantacaoId} consulta={consulta} />
    </section>
  );
}

function CorpoDasRespostas({
  implantacaoId,
  consulta,
}: {
  implantacaoId: string;
  consulta: ConsultaDaInfra;
}) {
  if (consulta.isPending) {
    return (
      <p className="cartao__texto" role="status">
        Carregando as respostas…
      </p>
    );
  }
  if (consulta.isError) {
    return (
      <Aviso tom="erro" data-testid="falha-das-respostas-da-infra">
        {mensagemDoConsole(consulta.error)}
      </Aviso>
    );
  }

  const dados = consulta.data;
  if (!dados.disponivel) {
    return (
      <p className="cartao__texto" data-testid="infra-sem-questionario">
        Esta implantação não tem questionário de infraestrutura.
      </p>
    );
  }

  const enviadaEm = formatarInstante(dados.enviada_em);
  return (
    <>
      <p className="cartao__texto mb-2" data-testid="situacao-da-infra">
        {infraEnviada(dados)
          ? `Enviado pelo cliente${enviadaEm ? ` em ${enviadaEm}` : ""}.`
          : dados.estado === "em_preenchimento"
            ? "O cliente está preenchendo."
            : "O cliente ainda não começou a preencher."}
      </p>
      {dados.respostas.length === 0 ? (
        <p className="cartao__texto" data-testid="infra-sem-respostas">
          Nada respondido até agora.
        </p>
      ) : (
        <dl className="lista-de-respostas-da-infra">
          {dados.respostas.map((resposta) => (
            <div key={resposta.pergunta_id} data-testid={`resposta-da-infra-${resposta.pergunta_id}`}>
              <dt>{resposta.enunciado}</dt>
              <dd>
                {resposta.credencial ? (
                  resposta.guardada ? (
                    <>
                      <Chip estado="ok">Guardada no cofre</Chip>{" "}
                      <Link to={caminhoDoCofre(implantacaoId)} data-testid="credencial-no-cofre">
                        Abrir o cofre
                      </Link>
                    </>
                  ) : (
                    <Chip estado="atencao">Não está mais no cofre — peça a senha atual ao cliente</Chip>
                  )
                ) : (
                  resposta.linhas.map((linha, indice) => <div key={indice}>{linha}</div>)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}
